import React, { useState, useRef, useEffect } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import {
  DoneIcon,
  LoginIcon,
  Visibility,
  VisibilityOff,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  MenuItem,
  IconButton,
  InputAdornment,
  Link,
  Divider,
  CircularProgress,
  Chip,
} from "../../../common/MuiImports";
import {
  UsernameField,
  PasswordField,
  FirstNameField,
  LastNameField,
  EmailField,
  PhoneNumberField,
} from "../../../common/AuthFields";
import BraintreeHostedFieldsForm, {
  BraintreeHostedFieldsFormRef,
} from "../../../common/BraintreeHostedFieldsForm";
import ModemAgreement from "./ModemAgreement";

import { Product, Device } from "../pages/BroadbandCheckoutPage";

import { useQueryString } from "../../../../hooks/useQueryString";

import {
  createCustomerService,
  getLoginCredentials,
} from "../../../../services/apiServices";

import VisaLogo from "../../../../assets/images/visa_icon.png";
import MastercardLogo from "../../../../assets/images/mc_icon.png";
import AmexLogo from "../../../../assets/images/amex_icon.png";
import DiscoverLogo from "../../../../assets/images/discover_icon.png";
import PaypalLogo from "../../../../assets/images/paypal_logo.svg";

import { useBraintreeForm } from "../../../../hooks/useBraintreeForm";
import { useBoingoForm } from "../../../../hooks/useBoingoForm";
import { MERCHANT_NAME_CONSTANT } from "../../../../assets/const/merchant-name.const";

import ApplePayForm, { ApplePayFormRef } from "../../../common/ApplePayForm";
import { useApplePay } from "../../../../hooks/useApplePay";

import { useAuthentication } from "../../../../hooks/useAuthentication";
import SignupDialog from "../../../common/SignupDialog";
import GetUserOnline from "../../../common/GetUserOnline";
import {
  formatProductCodes,
  getGsid,
  setCookieBase64,
} from "../../../../utils/utils";
import {
  formatCountryCode,
  formatPhoneNumberForApi,
  formatPhoneNumberWithCountryCode,
} from "../../../../utils/phoneNumberFormatter";
import PaypalForm, { PaypalFormRef } from "../../../common/PaypalForm";
import { usePayPal } from "../../../../hooks/usePayPal";
import { SELECT_COUNTRY_CODES } from "../../../../assets/const/countries.const";
import { useLocation, useParams } from "react-router-dom";
import { USER_EVENTS } from "../../../../utils/eventConstants";
import eventBus from "../../../../utils/eventBus";

interface BroadbandSignupFormData {
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  email: string;
  agreeTerms: boolean;
  payment_braintree?: {
    nonce: string;
    device_data: string;
    payment_type: string;
    country_code: string;
    postal_code?: string;
  };
  save_payment_info?: boolean;
  sms_opt_in?: boolean;
  subscription_opt_in: boolean;
  phone_number: string;
  country_code: string;
  country_code_verbose: string;
  discount_code?: string;
}

interface VenueDetails {
  venue_id: string;
  venue_sublocation: string;
  configs: {
    mail_message_id: string;
    gloop_id: string;
    scc: string;
    network_settings: networkSettingsProps;
  };
  modem?: {
    modem_agreement?: string;
    product_code?: string;
  };
  template?: {
    internet_sales_desktop: string;
  };
  products?: Product[];
}

interface networkSettingsProps {
  [key: string]: string;
}
interface LoginDataProps {
  username?: string;
  password?: string;
  venue_id?: string;
  bypass_login?: boolean;
  venue_sublocation?: string;
  mac?: string;
  ap_mac?: string;
  FirstName?: string;
  Email?: string;
  link_login_only?: string;
  ip?: string;
  config?: {
    network_settings: networkSettingsProps;
  };
}

interface BroadbandSignupFormProps {
  data: VenueDetails;
  selectedProduct: {
    byPassLogin?: boolean;
    plan_name: string;
    selected_duration?: string;
    product_code: string;
    additional_devices?: string | { product_code: string }[];
    original_price?: string;
    discount?: {
      promo_code: string;
      discounted_price?: string;
    };
    isFreeBulk?: boolean;
    custom_welcome_page?: string;
    speed?: string;
    speed_unit?: string;
  } | null;
  totalPrice: number;
  validPromoCode: string | null;
}

const ADDTL_DEVICE_CONST = "Additional_Device";

const BroadbandSignupForm: React.FC<BroadbandSignupFormProps> = ({
  data,
  selectedProduct,
  totalPrice,
  validPromoCode,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  //for testing deadman login
  const [testDeadmanLogin, setTestDeadmanLogin] = useState(false);

  const { getQueryParams } = useQueryString();
  const queryParams = getQueryParams();
  const queryParamsLowerCase = getQueryParams({ lowerCaseKey: true });

  const location = useLocation();
  const dataLayerPushed = useRef(false);

  useEffect(() => {
    dataLayerPushed.current = false;
  }, [location.pathname]);

  const {
    control,
    handleSubmit,
    errors,
    handleUsernameBlur,
    trigger,
    setValue,
    watch,
  } = useBoingoForm<BroadbandSignupFormData>({
    defaultValues: {
      country_code_verbose: formatCountryCode(SELECT_COUNTRY_CODES.default),
    },
  });

  const {
    isCustomerCreated,
    customerLoginData,
    dialogMessage,
    handleAuthSuccess,
    handleAuthError,
    initiateAuthentication,
    setDialogMessage,
    deadmanLogin,
  } = useAuthentication();

  useEffect(() => {
    // push final customer info to data layer on order completion
    if (isCustomerCreated && customerLoginData) {
      handleCheckoutSuccess();
    }
  }, [isCustomerCreated, customerLoginData]);

  const { isPaymentValid, braintreeFormRef, handleValidityChange } =
    useBraintreeForm();

  const {
    applePaymentNonce,
    applePayDeviceData,
    postalCode,
    applePayFormRef,
    handleApplePaySuccess,
    handleApplePayError,
    handleApplePayClick,
    resetApplePay,
  } = useApplePay<BroadbandSignupFormData>({
    setValue,
    setDialogMessage,
    setOpenDialog,
  });

  const {
    paypalPaymentNonce,
    paypalDeviceData,
    paypalFormRef,
    handlePayPalSuccess,
    handlePayPalError,
    resetPayPal,
  } = usePayPal<BroadbandSignupFormData>({
    setValue,
    setDialogMessage,
    setOpenDialog,
  });

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const isMonthlyPlan =
    (!selectedProduct?.selected_duration ||
      selectedProduct.selected_duration === "monthly") &&
    !data.modem;

  const handleCheckoutSuccess = () => {
    const selectedProducts = [
      selectedProduct || "",
      selectedProduct?.additional_devices &&
        selectedProduct?.additional_devices[0],
    ].filter((item) => item !== undefined);

    // Format the selected products for GA4 data layer
    const mappedProducts = selectedProducts?.map((code: any) => {
      if (!code?.product_code?.includes(ADDTL_DEVICE_CONST)) {
        return {
          item_id: code?.product_code || "",
          quantity: 1,
          item_name: code?.plan_name,
          price: code?.original_price || "",
        };
      } else {
        return {
          item_id: code?.product_code || "",
          quantity: 1,
          item_name: code?.device_detail_name,
          price: code?.device_price || "",
        };
      }
    });
    const payload = {
      event: "ecom_transaction",
      SessionID: getGsid(), // use GSID as the unique session ID
      value: totalPrice || "",
      items: mappedProducts,
    };

    if (!dataLayerPushed.current) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      dataLayerPushed.current = true;
    }
  };

  const onSubmit: SubmitHandler<BroadbandSignupFormData> = async (formData) => {
    setIsSubmitting(true);
    setOpenDialog(true);
    setDialogMessage("Creating User...");

    try {
      let customerData;
      let paymentData;

      const { scc: queryScc } = queryParamsLowerCase;
      const requestScc = queryScc !== undefined ? queryScc : data.configs.scc;

      const { agreeTerms, ...customerFormData } = formData;

      const baseCustomerData = {
        ...customerFormData,
        product_codes: formatProductCodes(
          selectedProduct?.product_code || "",
          selectedProduct?.additional_devices as Device[],
          data.modem?.product_code
        ),
        mail_message_id: data.configs.mail_message_id,
        venue_id: data.configs.gloop_id,
        scc: requestScc,
        save_payment_info:
          selectedProduct?.selected_duration === "monthly"
            ? true
            : formData.save_payment_info,
        sms_opt_in: formData.sms_opt_in,
        subscription_opt_in: formData.subscription_opt_in,
        phone_number: formatPhoneNumberForApi(
          formData.phone_number,
          formData.country_code_verbose
        ),
      };
      //if free plan is selected, do not include payment data
      if (selectedProduct?.plan_name.toLowerCase() === "free") {
        customerData = baseCustomerData;
      } else {
        if (applePaymentNonce) {
          paymentData = {
            nonce: applePaymentNonce,
            device_data: applePayDeviceData,
            payment_type: "Apple Pay",
            country_code: formData.country_code,
            postal_code: postalCode, //required for apple pay
          };
        } else if (paypalPaymentNonce) {
          paymentData = {
            nonce: paypalPaymentNonce,
            device_data: paypalDeviceData,
            payment_type: "PayPal",
            country_code: formData.country_code,
          };
        } else {
          //if hosted fields is selected
          const { payload, deviceData } =
            await braintreeFormRef.current!.tokenize();

          paymentData = {
            nonce: payload.nonce,
            device_data: deviceData,
            payment_type: "Credit Card",
            country_code: formData.country_code,
          };
        }

        customerData = {
          ...baseCustomerData,
          payment_braintree: paymentData,
        };

        // Apply discount if monthly plan is selected and there is discount code available from json, or validPromoCode is available from manual input
        if (
          selectedProduct?.selected_duration === "monthly" &&
          validPromoCode
        ) {
          customerData.discount_code = validPromoCode;
        }
      }

      // Check if there are no form validation errors
      if (Object.keys(errors).length === 0) {
        //smartzone login is determined by the presence of nbiIP and uip in queryParams
        //bwpromo uses mac from network controller to get login credentials
        //wlc uses ap_mac from network controller to get login credentials, and switch_url from network controller for link_login_only
        const isSmartzoneLogin = Boolean(queryParams.nbiIP && queryParams.uip);
        const isWLCLogin = Boolean(queryParams.switch_url);
        const isVia = !isWLCLogin && !isSmartzoneLogin;
        let client_mac = "00:00:00:00:00:00";
        let ap_mac = "00:00:00:00:00:00";
        const byPassLogin = selectedProduct?.byPassLogin;

        if (isVia) {
          client_mac =
            queryParams.mac !== undefined ? queryParams.mac : client_mac;
          ap_mac =
            queryParams.ap_mac !== undefined ? queryParams.ap_mac : ap_mac;
        }
        if (isSmartzoneLogin) {
          client_mac =
            queryParams.client_mac !== undefined
              ? queryParams.client_mac
              : client_mac;
          ap_mac = queryParams.mac !== undefined ? queryParams.mac : ap_mac;
        }
        if (isWLCLogin) {
          client_mac =
            queryParams.client_mac !== undefined
              ? queryParams.client_mac
              : client_mac;
          ap_mac =
            queryParams.ap_mac !== undefined ? queryParams.ap_mac : ap_mac;
        }
        try {
          // Call createCustomerService with the prepared data and test mode flag
          const createCustomerResponse = await createCustomerService(
            customerData,
            testDeadmanLogin
          );

          console.log("Customer creation response:", createCustomerResponse);
          setDialogMessage("User created successfully!");

          if (createCustomerResponse.status === 201) {
            eventBus.dispatch(USER_EVENTS.USER_SIGNUP, {
              ...customerData,
              venue_id: data.venue_id,
              action_type: "create_customer"
            });
            //set token cookie for welcome page
            const scalData = {
              token: createCustomerResponse?.data?.selfcare_token,
            };

            setCookieBase64("scal", scalData, 15);
            if (selectedProduct?.custom_welcome_page) {
              setCookieBase64(
                "wpage",
                selectedProduct.custom_welcome_page,
                1440
              );
              // Store product speed data for welcome template
              const productSpeedData = {
                speed: selectedProduct.speed,
                speed_unit: selectedProduct.speed_unit
              };
              setCookieBase64("product_speed", productSpeedData, 1440);
              
              if (selectedProduct.product_code.toLowerCase().includes("aetc")) {
                setCookieBase64("template", "aetc", 1440);
              }
              if (
                selectedProduct.product_code.toLowerCase().includes("army_free")
              ) {
                setCookieBase64("template", "army", 1440);
              }
            }

            // Initialize prepareLoginData without bypass_login initially
            let prepareLoginData: LoginDataProps = {
              username: customerData.username,
              password: customerData.password,
              venue_id: data.venue_id,
              venue_sublocation: data.venue_sublocation,
              mac: client_mac,
              config: {
                network_settings: data.configs.network_settings,
              },
            };

            const loginCredentialsResponse = await getLoginCredentials(
              prepareLoginData,
              "military_subscriber"
            );

            console.log(
              "Login credentials response:",
              loginCredentialsResponse
            );

            // initiate authentication using login credentials for VIA, vSZ, or wlc controllers
            initiateAuthentication({
              username: loginCredentialsResponse.data.username,
              password: loginCredentialsResponse.data.password,
              isWLC: isWLCLogin ? true : false,
              ...(isSmartzoneLogin
                ? {
                    isSmartzoneLogin: true,
                    ip: queryParams.uip,
                    mac: client_mac,
                    link_login_only: queryParams.nbiIP,
                  }
                : { isSmartzoneLogin: false }),
              ...(byPassLogin === true && { bypass_login: byPassLogin }),
            });
          }
        } catch (error: any) {
          console.error("Error creating customer:", error);
          eventBus.dispatch(USER_EVENTS.USER_SIGNUP_ERROR, {
            error,
            venue_id: data.venue_id,
            action_type: "create_customer"
          });

          //If API is down, initiate deadman login
          if (
            (error.response && error.response.status === 502) ||
            (error.response && error.status === 502)
          ) {
            const deadmanLoginResponse = await deadmanLogin({
              venue_id: data.venue_id,
              configs: data.configs,
              username: customerData.username,
              password: customerData.password,
              isWLC: isWLCLogin ? true : false,
              ...(isSmartzoneLogin
                ? {
                    isSmartzoneLogin: true,
                    ip: queryParams.uip,
                    mac: client_mac,
                    link_login_only: queryParams.nbiIP,
                  }
                : { isSmartzoneLogin: false }),
              degraded: true,
            });

            console.log("Deadman login response:", deadmanLoginResponse);
            // get login credentials from deadman login response from bwpromo and vSZ/wLC/via controllersnand login users
            initiateAuthentication(deadmanLoginResponse);
          }
          setDialogMessage(
            `Error creating user. Please try again. ${error.message}`
          );
          setOpenDialog(true);
        }
      } else {
        console.error("Form validation errors:", errors);
        setDialogMessage("Please correct the form errors and try again.");
        setOpenDialog(true);
      }
    } catch (error: any) {
      console.error("Error processing form:", error);
      setDialogMessage(error.message || "An error occurred. Please try again.");
      setOpenDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        data-test-id="signup-form"
      >
        {/* DO NOT DELETE: Add test mode checkbox for deadman signup/testing */}
        {/* <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={testDeadmanLogin}
                onChange={(e) => setTestDeadmanLogin(e.target.checked)}
                data-test-id="test-deadman-login-checkbox"
              />
            }
            label="Test Deadman Login (Simulate 502 Error)"
            sx={{ color: "#545861" }}
          />
        </Box> */}

        {selectedProduct?.plan_name.toLowerCase() !== "free" && (
          <Box className="paymentContainer" data-test-id="payment-container">
            <Typography
              variant="h5"
              sx={{ color: "#545861" }}
              gutterBottom
              data-test-id="payment-title"
            >
              Enter payment
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 2,
                position: "relative",
              }}
              data-test-id="express-checkout-container"
            >
              <Typography
                variant="body2"
                align="center"
                data-test-id="express-checkout-title"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                  backgroundColor: "#f5f5f5",
                  margin: "auto",
                  position: "absolute",
                  fontSize: "16px",
                  top: "0px",
                  color: "var(--boingo-grey)"
                }}
              >
                Express Checkout
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "80%",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  margin: "auto",
                  marginY: 4,
                  flexDirection: "column",
                  alignItems: "center",
                  px: 2,
                  py: 3,
                }}
              >
                {window.ApplePaySession &&
                  window.ApplePaySession.canMakePayments() && (
                    <>
                      <Button
                        onClick={handleApplePayClick}
                        variant="contained"
                        data-test-id="apple-pay-button"
                        sx={{
                          "-webkit-appearance": "-apple-pay-button",
                          "-apple-pay-button-style": "black",
                          width: "100%",
                          height: "42px",
                          color: "#fff",
                          marginBottom: 2,
                        }}
                      ></Button>
                      <ApplePayForm
                        ref={applePayFormRef}
                        onSuccess={handleApplePaySuccess}
                        onError={handleApplePayError}
                        amount={totalPrice}
                        merchantName={MERCHANT_NAME_CONSTANT}
                      />
                    </>
                  )}
                <PaypalForm
                  ref={paypalFormRef}
                  onSuccess={handlePayPalSuccess}
                  onError={handlePayPalError}
                  amount={totalPrice}
                  merchantName={MERCHANT_NAME_CONSTANT}
                />
              </Box>
            </Box>

            {!applePaymentNonce && !paypalPaymentNonce && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Divider sx={{ flexGrow: 1 }} />
                  <Typography variant="body2" sx={{ mx: 2 }}>
                    OR
                  </Typography>
                  <Divider sx={{ flexGrow: 1 }} />
                </Box>
                <Typography variant="h5" sx={{ color: "#545861" }}>
                  Credit card
                </Typography>
                <Box
                  className="credit-card-logos"
                  data-test-id="credit-card-logos"
                  sx={{
                    display: "flex",
                    justifyContent: "left",
                    marginY: 3,
                    gap: "10px",
                    flexWrap: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {[VisaLogo, MastercardLogo, AmexLogo, DiscoverLogo].map(
                    (logo, index) => (
                      <img
                        key={index}
                        src={logo}
                        alt={`Credit Card ${index + 1}`}
                        height="40"
                        style={{ objectFit: "contain" }}
                      />
                    )
                  )}
                </Box>
                <BraintreeHostedFieldsForm
                  ref={braintreeFormRef}
                  control={control}
                  onValidityChange={handleValidityChange}
                  data-test-id="braintree-form"
                />
              </>
            )}

            {(applePaymentNonce || paypalPaymentNonce) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  flexDirection: "column",
                }}
              >
                <Chip
                  icon={<DoneIcon />}
                  label={`${
                    applePaymentNonce ? "Apple Pay" : "PayPal"
                  } is selected`}
                  variant="outlined"
                  color="success"
                  sx={{
                    marginY: 1,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    marginY: 1,
                    textAlign: "center",
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                  }}
                  onClick={() => {
                    if (applePaymentNonce) {
                      resetApplePay();
                    } else {
                      resetPayPal();
                    }
                  }}
                >
                  or select new payment
                </Typography>
              </Box>
            )}

            {/* Conditionally render the savePaymentInfo checkbox */}
            {!isMonthlyPlan && (
              <Controller
                name="save_payment_info"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ color: "#545861" }}
                    control={<Checkbox {...field} />}
                    label=" Save this card for future use"
                  />
                )}
              />
            )}
          </Box>
        )}

        {/* Add modem agreement section before "Create your account" */}
        {data?.modem?.modem_agreement && (
          <ModemAgreement modemAgreement={data.modem.modem_agreement} />
        )}

        {selectedProduct?.isFreeBulk ? (
          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 6, color: "#545861" }}
            data-test-id="account-creation-title"
          >
            Create your FREE account
          </Typography>
        ) : (
          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 6, color: "#545861" }}
            data-test-id="account-creation-title"
          >
            Create your account
          </Typography>
        )}

        <UsernameField
          control={control}
          errors={errors}
          onUsernameBlur={handleUsernameBlur}
          trigger={trigger}
        />

        <PasswordField control={control} errors={errors} />
        <Box sx={{ my: 8 }}></Box>
        <FirstNameField control={control} errors={errors} />
        <LastNameField control={control} errors={errors} />
        <EmailField control={control} errors={errors} />
        <PhoneNumberField control={control} errors={errors} trigger={trigger} />

        <Box
          sx={{
            "& .MuiFormControlLabel-root": {
              margin: "10px 0 0 0",
            },
            "& .MuiTypography-root": {
              margin: 0,
            },
            "& .MuiCheckbox-root": {
              margin: 0,
              padding: "0 8px 0 0",
            },
          }}
        >
          <Controller
            name="sms_opt_in"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    sx={{
                      alignSelf: "flex-start",
                      marginTop: "0px", // Adjust this value as needed
                    }}
                    data-test-id="sms-opt-in-checkbox"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#545861",
                      marginTop: "10px", // Adjust this value to match the checkbox
                    }}
                  >
                    By checking this box, you consent to receive updates and
                    marketing communications via SMS. You can change your
                    preferences at any time.
                  </Typography>
                }
                sx={{
                  alignItems: "flex-start",
                  marginTop: 2,
                }}
              />
            )}
          />
          {selectedProduct?.plan_name.toLowerCase() !== "free" && selectedProduct?.selected_duration === "monthly" && (
            <Controller
              name="subscription_opt_in"
              control={control}
              defaultValue={false}
              rules={{
                required: "You must agree to the renewal notice.",
              }}
              render={({ field }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{
                          alignSelf: "flex-start",
                        }}
                        {...field}
                        checked={field.value}
                        data-test-id="subscribe-checkbox"
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          trigger("subscription_opt_in");
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#545861",
                          marginTop: "10px",
                          alignSelf: "flex-start",
                        }}
                      >
                        {selectedProduct?.discount
                          ? `This subscription will renew monthly at $${selectedProduct?.original_price} after your first month at $${selectedProduct?.discount?.discounted_price}, until I cancel, and I can cancel any time.`
                          : `This subscription will renew monthly at ${selectedProduct?.original_price} until I cancel, and I can cancel any time.`}
                      </Typography>
                    }
                  />
                  {errors.subscription_opt_in && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 1, ml: 4 }}
                      data-test-id="subscription-opt-in-error"
                    >
                      {errors.subscription_opt_in.message}
                    </Typography>
                  )}
                </Box>
              )}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            data-test-id="complete-order-button"
            sx={{
              paddingY: 1.5,
              color: "white",
              fontFamily: "Figtree-Bold",
              fontSize: "22px",
              borderRadius: "6px",
              backgroundColor: "#d32f2f",
              width: { xs: "100%", md: "260px" },
            }}
            disabled={
              (selectedProduct?.plan_name.toLowerCase() === "free"
                ? false
                : !applePaymentNonce &&
                  !isPaymentValid &&
                  !paypalPaymentNonce) ||
              Object.keys(errors).length > 0 ||
              isSubmitting ||
              (selectedProduct?.plan_name.toLowerCase() !== "free" && 
               selectedProduct?.selected_duration === "monthly" && 
               !watch("subscription_opt_in"))
            }
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <span>
                {selectedProduct?.isFreeBulk || selectedProduct?.plan_name.toLowerCase() === "free" 
                  ? "Connect to Wi-Fi"
                  : selectedProduct?.selected_duration === "monthly" && !selectedProduct?.isFreeBulk && selectedProduct?.plan_name.toLowerCase() !== "free" ?
                  "Agree and subscribe" :
                  "Complete Purchase"
                }
              </span>
            )}
          </Button>
        </Box>

        <Typography
          variant="body2"
          sx={{ marginTop: 2, marginBottom: 15, color: "var(--boingo-grey)" }}
          data-test-id="terms-agreement"
        >
          By clicking submit, I agree to the{" "}
          <Link
            sx={{ color: "#d32f2f", textDecoration: "none" }}
            href="https://www.boingo.com/legal/end-user-license-agreement-boingo-customer-agreement/"
            data-test-id="terms-link"
          >
            Boingo End User License Agreement.{" "}
          </Link>
        </Typography>
      </Box>

      <SignupDialog
        open={openDialog}
        onClose={handleCloseDialog}
        dialogMessage={dialogMessage}
        isSubmitting={isSubmitting}
        data-test-id="signup-dialog"
      />
      {/*  customerLoginData is set in the useAuthentication hook  of initiateAuthentication */}
      {isCustomerCreated && customerLoginData && (
        <GetUserOnline
          customerLoginData={customerLoginData}
          onSuccess={handleAuthSuccess}
          onError={handleAuthError}
        />
      )}
    </>
  );
};

export default BroadbandSignupForm;
